import { formatDatetime } from "@/helpers/time";
import useNumberFormatter from "@/hooks/useNumberFormatter";
import { Invoice } from "@/types/types";
import {
  Box,
  Button,
  CloseButton,
  Dialog,
  HStack,
  Heading,
  Portal,
  Separator,
  Text,
  VStack,
} from "@chakra-ui/react";
import { mapPaymentMethod, mapPaymentSource } from "../mappers/invoice.mapper";

const reverseMapPaymentMethod = Object.entries(mapPaymentMethod).reduce(
  (acc, [key, value]) => ({ ...acc, [value]: key }),
  {}
);
const reverseMapPaymentSource = Object.entries(mapPaymentSource).reduce(
  (acc, [key, value]) => ({ ...acc, [value]: key }),
  {}
);

type Props = {
  open: boolean;
  onClose: () => void;
  paymentInfoInvoice?: Invoice;
};

const PaymentInfoModal = ({
  open,
  onClose,
  paymentInfoInvoice,
}: Props): JSX.Element => {
  const formatNumber = useNumberFormatter("es-CO", "COP", "number");

  if (!paymentInfoInvoice) return <></>;

  const paymentInfo = [
    {
      label: "ID del recaudo",
      value: paymentInfoInvoice.id || "-",
    },
    {
      label: "ID del pago",
      value: paymentInfoInvoice.paymentId || "-",
    },
    {
      label: "Fecha de pago",
      value:
        (paymentInfoInvoice.payedAt &&
          formatDatetime(paymentInfoInvoice.payedAt)) ||
        paymentInfoInvoice.paymentDate ||
        "-",
    },
    {
      label: "Valor pagado",
      value: paymentInfoInvoice.paymentAmount
        ? formatNumber(paymentInfoInvoice.paymentAmount)
        : "-",
    },
    {
      label: "Método de pago",
      value:
        reverseMapPaymentMethod[
          paymentInfoInvoice.paymentMethod as keyof typeof reverseMapPaymentMethod
        ] || "-",
    },
    {
      label: "Plataforma",
      value:
        reverseMapPaymentSource[
          paymentInfoInvoice.paymentSource as keyof typeof reverseMapPaymentSource
        ] || "-",
    },
    {
      label: "Fecha de abono",
      value: paymentInfoInvoice.settlementDate || "-",
    },
    {
      label: "Ciclo",
      value: paymentInfoInvoice.settlementTime || "-",
    },
  ];

  return (
    <Dialog.Root
      open={open}
      onOpenChange={(e) => !e.open && onClose()}
      size="md"
    >
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content>
            <Dialog.Header borderBottom="1px solid #ebecef80" padding="4">
              <Dialog.Title>
                <Heading>Información de Pago</Heading>
              </Dialog.Title>
            </Dialog.Header>
            <Dialog.CloseTrigger asChild>
              <CloseButton marginTop="1" onClick={onClose} />
            </Dialog.CloseTrigger>
            <Dialog.Body py={6}>
              <VStack gap={4} align="stretch">
                {paymentInfo.map((info, index) => (
                  <Box key={index}>
                    <HStack justify="space-between" mb={2}>
                      <Text fontWeight={600} color="gray.600">
                        {info.label}:
                      </Text>
                      <Text color="gray.700">{info.value}</Text>
                    </HStack>
                    {index < paymentInfo.length - 1 && (
                      <Separator borderColor="gray.200" />
                    )}
                  </Box>
                ))}
              </VStack>
            </Dialog.Body>
            <Dialog.Footer borderTop="1px solid #ebecef80">
              <Button onClick={onClose}>Cerrar</Button>
            </Dialog.Footer>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
};

export default PaymentInfoModal;
